import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import LoadingIndicator from '../../components/LoadingIndicator';
import SidebarContainer, {
  SidebarContainerProps,
} from '../../containers/SidebarContainer';
import { analyzeGateUsageSchedule } from '../store/analyze.state';
import DockUsageStatisticsPanel from './DockUsageStatisticsSidebar';
import { scheduleHiddenDocksState } from './schedule.state';

const SimulationDockStatsSidebar: React.FC = () => {
  const { t } = useTranslation('simulation');
  const schedule = useRecoilValue(analyzeGateUsageSchedule);
  const [hiddenDocks, setHiddenDocks] = useRecoilState(
    scheduleHiddenDocksState,
  );

  // Convert array to Set for compatibility with existing DockUsageStatisticsPanel
  const safeHiddenDocks = React.useMemo(() => {
    // Handle cases where persistent storage might return unexpected types
    if (!hiddenDocks) return new Set<string>();
    if (!Array.isArray(hiddenDocks)) {
      console.warn('scheduleHiddenDocksState is not an array:', hiddenDocks);
      return new Set<string>();
    }
    return new Set(hiddenDocks);
  }, [hiddenDocks]);

  // Extract dock stats from the schedule data
  const dockStats = React.useMemo(() => {
    if (!schedule?.resources?.length) {
      return {
        distinctCount: 0,
        counts: [],
      };
    }

    const dockCounts = schedule.resources.reduce(
      (acc, resource) => {
        const dockId = resource.id;
        const jobCount = resource.assignments?.length || 0;
        acc[dockId] = (acc[dockId] || 0) + jobCount;
        return acc;
      },
      {} as Record<string, number>,
    );

    const sortedCounts = Object.entries(dockCounts)
      .sort(([, a], [, b]) => b - a) // Sort by count descending
      .map(([dock, count]) => [dock, count] as [string, number]);

    return {
      distinctCount: Object.keys(dockCounts).length,
      counts: sortedCounts,
    };
  }, [schedule]);

  const toggleDockVisibility = (dock: string) => {
    setHiddenDocks(prev => {
      // Handle cases where persistent storage might return unexpected types
      let currentArray: string[] = [];
      if (prev && Array.isArray(prev)) {
        currentArray = prev;
      } else if (prev) {
        console.warn(
          'scheduleHiddenDocksState is not an array in toggle:',
          prev,
        );
        currentArray = [];
      }

      const currentSet = new Set(currentArray);
      if (currentSet.has(dock)) {
        return currentArray.filter(d => d !== dock);
      } else {
        return [...currentArray, dock];
      }
    });
  };

  const containerProps: SidebarContainerProps = {
    type: 'sidebar-simulation-schedule-docks-info',
    title: t`Dock Usage Statistics`,
    size: 'sm',
    sideRight: true,
  };

  return (
    <SidebarContainer {...containerProps}>
      <Suspense fallback={<LoadingIndicator message={t`Loading Dock Stats`} />}>
        <DockUsageStatisticsPanel
          dockStats={dockStats}
          hiddenDocks={safeHiddenDocks}
          toggleDockVisibility={toggleDockVisibility}
        />
      </Suspense>
    </SidebarContainer>
  );
};

export default SimulationDockStatsSidebar;
